import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Thead, Tbody, Tr, Th, Td, Box, Heading, Flex, Select, Input, Spinner, IconButton, Badge, Tooltip, useToast, useTheme } from "@chakra-ui/react";
import { FaPlus, FaBan, FaWarehouse } from "react-icons/fa";
import AddMissingStoreModal from "./AddMissingStoreModal";

const MissingStores = () => {
  const toast = useToast();
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);

  const retailers = ["PNP", "CHK", "DIS", "CLI", "MAK"];

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/validation/get-missing-stores`, { selectedRetailer });
      setData(response.data.data);
      setRegions(response.data.regions);
      setIsLoading(false);
    } catch (error) {
      toast({ title: "Failed to load data", description: "Technical error occurred", status: "error", duration: 3000, isClosable: true });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedRetailer]);

  const filteredData = data.filter((row) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return row.site_code.toLowerCase().includes(term) || row.site_description.toLowerCase().includes(term) || (row.suppliers && row.suppliers.toLowerCase().includes(term));
  });

  const handleAddClick = (store) => {
    setSelectedStore(store);
    setIsModalOpen(true);
  };

  const handleExclude = async (store) => {
    try {
      const siteCode = `%${store.site_code}%`;
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/ins-excluded-store`, {
        retailer: store.retailer,
        site_code: siteCode,
      });
      toast({ title: "Store excluded", description: `${store.site_code} added to excluded stores.`, status: "success", duration: 3000, isClosable: true });
      fetchData();
    } catch (error) {
      toast({ title: "Failed to exclude store", description: error?.response?.data?.message || "An error occurred.", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleDc = async (store) => {
    try {
      const siteCode = `%${store.site_code}%`;
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/ins-dc-store`, {
        retailer: store.retailer,
        site_code: siteCode,
      });
      toast({ title: "DC store added", description: `${store.site_code} added to DC stores.`, status: "success", duration: 3000, isClosable: true });
      fetchData();
    } catch (error) {
      toast({ title: "Failed to add DC store", description: error?.response?.data?.message || "An error occurred.", status: "error", duration: 3000, isClosable: true });
    }
  };

  return (
    <Flex minWidth="75vw">
      <Box flex="1" maxHeight="700px" overflowY="auto">
        <Heading size="md" textAlign="center" mb={4} bg={theme.colors.primary2} color="white" p={1}>
          Missing Stores <Badge ml={2} colorScheme="red" fontSize="md">{filteredData.length}</Badge>
        </Heading>
        {isLoading ? (
          <Flex justify="center" p={8}><Spinner size="lg" /></Flex>
        ) : (
          <Table variant="striped" size="sm" colorScheme="custom" border="1px" borderColor="gray.200" borderRadius="md">
            <Thead>
              <Tr>
                <Th>Site Code</Th>
                <Th>Site Description</Th>
                <Th>Retailer</Th>
                <Th>Supplier(s)</Th>
                <Th>Add Region</Th>
                <Th>Exclude</Th>
                <Th>DC</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredData.map((row, index) => (
                <Tr key={index}>
                  <Td>{row.site_code}</Td>
                  <Td>{row.site_description}</Td>
                  <Td>{row.retailer}</Td>
                  <Td fontSize="xs">{row.suppliers}</Td>
                  <Td>
                    <Tooltip label="Add to store regions">
                      <IconButton icon={<FaPlus />} onClick={() => handleAddClick(row)} colorScheme="green" variant="ghost" size="sm" aria-label="Add store" />
                    </Tooltip>
                  </Td>
                  <Td>
                    <Tooltip label="Add to excluded stores">
                      <IconButton icon={<FaBan />} onClick={() => handleExclude(row)} colorScheme="orange" variant="ghost" size="sm" aria-label="Exclude store" />
                    </Tooltip>
                  </Td>
                  <Td>
                    <Tooltip label="Add to DC stores">
                      <IconButton icon={<FaWarehouse />} onClick={() => handleDc(row)} colorScheme="purple" variant="ghost" size="sm" aria-label="DC store" />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      <Box ml={4}>
        <Select placeholder="All Retailers" mb={4} value={selectedRetailer} onChange={(e) => setSelectedRetailer(e.target.value)}>
          {retailers.map((r, i) => (
            <option key={i} value={r}>{r}</option>
          ))}
        </Select>
        <Input placeholder="Search" mb={4} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </Box>

      <AddMissingStoreModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        storeData={selectedStore || {}}
        regions={regions}
        refreshData={fetchData}
      />
    </Flex>
  );
};

export default MissingStores;
